import type React from "react"
import {
  type PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import styled from "styled-components"
import type { BaseProps } from "../types/props"

function getTooltipPlacement(placement: TooltipPlacement) {
  switch (placement) {
    case "top-left":
      return "transform: translate(0%, 0%);"
    case "top-center":
      return "transform: translate(-50%, 0%);"
    case "top-right":
      return "transform: translate(-100%, 0%);"
    case "middle-left":
      return "transform: translate(0%, -50%);"
    case "middle-center":
      return "transform: translate(-50%, -50%);"
    case "middle-right":
      return "transform: translate(-100%, -50%);"
    case "bottom-left":
      return "transform: translate(0%, -100%);"
    case "bottom-center":
      return "transform: translate(-50%, -100%);"
    case "bottom-right":
      return "transform: translate(-100%, -100%);"
  }
}

interface WrapperProps
  extends Pick<
    TooltipProps,
    | "width"
    | "height"
    | "padding"
    | "color"
    | "background"
    | "border"
    | "borderRadius"
    | "shadow"
  > {
  show: boolean
  position: TooltipPosition
  placement: TooltipPlacement
}
const Wrapper = styled.aside<WrapperProps>`
  visibility: ${(props) => (props.show ? "visible" : "hidden")};

  position: absolute;
  left: ${(props) => `${props.position.x}px`};
  top: ${(props) => `${props.position.y}px`};
  z-index: 1;

  ${(props) => getTooltipPlacement(props.placement)};
  pointer-events: none;

  width: ${(props) => props.width};
  height: ${(props) => props.height};
  padding: ${(props) => props.padding};

  border: ${(props) => props.border};
  border-radius: ${(props) =>
    props.borderRadius ?? props.theme.sizes.defaultBorderRadius};
  box-shadow: ${(props) => props.theme.layout.defaultShadow};

  color: ${(props) => props.color};
  background: ${(props) => props.background};
`

export interface TooltipPosition {
  x: number
  y: number
}
type PlacementVertical = "top" | "middle" | "bottom"
type PlacementHorizontal = "left" | "center" | "right"
export type TooltipPlacement = `${PlacementVertical}-${PlacementHorizontal}`

export interface TooltipProps extends BaseProps, PropsWithChildren<object> {
  placement?: TooltipPlacement

  width?: string
  height?: string
  padding?: string

  border?: string
  borderRadius?: string
  shadow?: string
  color?: string
  background?: string
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  className,
  id,

  placement = "top-left",

  width,
  height,
  padding = "0.5rem",

  border,
  borderRadius,
  shadow,
  color = "currentColor",
  background = "white",
}) => {
  const wrapperRef = useRef<HTMLElement>(null)

  const [show, setShow] = useState(false)
  const [pos, setPos] = useState<TooltipPosition>({ x: 0, y: 0 })

  useLayoutEffect(() => {
    const el = wrapperRef.current
    if (!el) return

    const parent = el.parentElement
    if (!parent) return

    const show = () => setShow(true)
    const hide = () => setShow(false)

    function update(evt: MouseEvent) {
      setPos({
        x: evt.offsetX,
        y: evt.offsetY,
      })
    }

    parent.addEventListener("mouseenter", show)
    parent.addEventListener("mouseleave", hide)
    parent.addEventListener("mousemove", update)

    return () => {
      parent.removeEventListener("mouseenter", show)
      parent.removeEventListener("mouseleave", hide)
      parent.removeEventListener("mousemove", update)
    }
  }, [])

  return (
    <Wrapper
      ref={wrapperRef}
      className={className}
      id={id}
      show={show}
      position={pos}
      placement={placement}
      width={width}
      height={height}
      padding={padding}
      border={border}
      borderRadius={borderRadius}
      shadow={shadow}
      color={color}
      background={background}
    >
      {children}
    </Wrapper>
  )
}
